import type { PackagingPlanRow, Week39PackagingLive, Week39PackagingSnapshot } from "./packaging-report-types.js";

import { isManualPackagingProductionDocument, productionLineFromDocument } from "./production-document-lines.js";
import { isPackagingWorkOrderPending, packagingWorkOrderKey } from "./packaging-work-order.js";
export { productionLineFromDocument } from "./production-document-lines.js";

export const UNMAPPED_PRODUCTION_LINE = "Hat eşleşmedi";

/** Transfer the produced part of a plan to its physical line; conserve every source cell total. */
export function routePackagingProduction(snapshot: Week39PackagingSnapshot, live: Week39PackagingLive | null): Week39PackagingSnapshot {
  if (!live || snapshot.productionRouted) return snapshot;
  snapshot = { ...snapshot, lines: snapshot.lines.map((line) => ({ ...line, rows: line.rows.map((row) => {
    const match = !isPackagingWorkOrderPending(row) && live.workOrderMatches?.find((candidate) =>
      candidate.excelWorkOrderNo === row.workOrderNo && candidate.productCode === row.productCode);
    return match ? { ...row, matchedWorkOrderNo: match.mikroWorkOrderNo } : row;
  }) })) };
  const end = new Date(`${snapshot.weekStart}T12:00:00Z`);
  end.setUTCDate(end.getUTCDate() + 6);
  const weekEnd = end.toISOString().slice(0, 10);
  const movements = new Map<string, Week39PackagingLive["items"]>();
  for (const item of live.items) {
    if (item.date < snapshot.weekStart || item.date > weekEnd || item.date < live.range.from || item.date > live.range.to || item.quantity === 0) continue;
    // A Flowpack (or unreviewed) document cannot move or fulfil an Elle Paketleme plan row.
    if (!isManualPackagingProductionDocument(item.documentNo)) continue;
    const key = packagingWorkOrderKey(item);
    const items = movements.get(key) ?? [];
    items.push(item); movements.set(key, items);
  }
  const grouped = new Map(snapshot.lines.map((line) => [line.name, { ...line, rows: [] as PackagingPlanRow[] }]));
  const append = (name: string, row: PackagingPlanRow) => {
    let line = grouped.get(name);
    if (!line) {
      line = { name, excelName: null, mappingStatus: "mapped", footerCell: null, rows: [] };
      grouped.set(name, line);
    }
    line.rows.push(row);
  };
  const primaryLines = new Map<string, Set<string>>();
  const pairPlans = new Map<string, PackagingPlanRow[]>();
  const routedPlans = new Map<number, { name: string; row: PackagingPlanRow }>();
  let virtualRow = -1;
  for (const line of snapshot.lines) for (const plan of line.rows) {
    const key = packagingWorkOrderKey(plan);
    if (plan.carryover) {
      // Carryover output was allocated to this exact prior-plan row before routing.
      // Keep that allocation once, and split only known physical-line output. The
      // untouched balance remains on the prior planned line with no fabricated day.
      const datedActuals = plan.carryover.actualsByDate;
      const byPhysicalLine = datedActuals === null || datedActuals.some((item) => item.productionLine === null)
        ? null
        : [...datedActuals.reduce((groups, item) => {
          const productionLine = item.productionLine;
          if (productionLine === null) return groups;
          const entries = groups.get(productionLine) ?? [];
          entries.push(item);
          groups.set(productionLine, entries);
          return groups;
        }, new Map<string, NonNullable<typeof datedActuals>>())].sort(([a], [b]) => a.localeCompare(b));
      if (byPhysicalLine?.length === 1) {
        const [physicalLine, actualsByDate] = byPhysicalLine[0];
        append(physicalLine, { ...plan, productionLine: physicalLine, plannedLineName: line.name, allocations: [],
          carryover: { ...plan.carryover, actualsByDate } });
      } else if (byPhysicalLine && byPhysicalLine.length > 1 && plan.carryover.quantity !== null) {
        let remainingTarget = plan.carryover.quantity;
        for (const [physicalLine, actualsByDate] of byPhysicalLine) {
          const actual = actualsByDate.reduce((sum, item) => sum + item.quantity, 0);
          const target = Math.min(remainingTarget, actual);
          remainingTarget -= target;
          append(physicalLine, { ...plan, sourceRow: virtualRow--, excelSourceRow: plan.sourceRow,
            productionLine: physicalLine, plannedLineName: line.name, allocations: [],
            carryover: { ...plan.carryover, quantity: target, weeklyActual: actual, actualsByDate } });
        }
        if (remainingTarget > 0) {
          append(line.name, { ...plan, sourceRow: virtualRow--, excelSourceRow: plan.sourceRow, allocations: [],
            carryover: { ...plan.carryover, quantity: remainingTarget, weeklyActual: 0, actualsByDate: [] } });
        }
      } else {
        // Unknown source line, unknown balance, or no observed output: do not infer a
        // physical line. Retaining the prior planned line is safer than misattribution.
        append(line.name, { ...plan, allocations: [] });
      }
      const plans = pairPlans.get(key) ?? [];
      plans.push(plan); pairPlans.set(key, plans);
      continue;
    }
    const items = [...(movements.get(key) ?? [])].sort((a, b) => a.date.localeCompare(b.date)
      || (a.recordedAt ?? "").localeCompare(b.recordedAt ?? "") || (a.documentNo ?? "").localeCompare(b.documentNo ?? ""));
    const names = [...new Set(items.map((item) => productionLineFromDocument(item.documentNo) ?? UNMAPPED_PRODUCTION_LINE))];
    const name = names.includes(line.name) ? line.name : names[0] ?? line.name;
    const routed = names.length ? { ...plan, productionLine: name, plannedLineName: line.name,
      allocations: plan.allocations.map((allocation) => ({ ...allocation })) } : plan;
    routedPlans.set(plan.sourceRow, { name, row: routed });
    const assigned = primaryLines.get(key) ?? new Set<string>();
    assigned.add(name); primaryLines.set(key, assigned);
    const plans = pairPlans.get(key) ?? [];
    plans.push(plan); pairPlans.set(key, plans);
  }
  for (const [key, items] of [...movements].sort(([a], [b]) => a.localeCompare(b))) {
    const plans = pairPlans.get(key);
    const template = plans?.[0];
    if (plans?.length && plans.every((plan) => plan.carryover)) continue;
    if (!template || !plans) {
      const references = (snapshot.excelReferences ?? []).filter((reference) => !isPackagingWorkOrderPending(reference));
      const exact = references.filter((reference) => packagingWorkOrderKey(reference) === key);
      const alternatives = references.filter((reference) => reference.productCode === items[0].productCode);
      const planExplanation = exact.length
        ? exact.every((reference) => !reference.hasDailyPlan) ? "Günlük plan dağılımı yok." : "Üretim günlük planla eşleşmedi."
        : alternatives.length
          ? "İş emri planla eşleşmedi."
          : "Günlük planla eşleşmeyen Mikro girişi.";
      const names = [...new Set(items.map((item) => productionLineFromDocument(item.documentNo)).filter((name) => name !== null))];
      const countries = [...new Set(items.map((item) => item.country?.trim()).filter((country) => Boolean(country)))];
      const country = countries.length === 1 ? countries[0] ?? null : null;
      for (const name of names) {
        const entries = items.filter((item) => productionLineFromDocument(item.documentNo) === name);
        const first = entries[0];
        append(name, { sourceRow: virtualRow--, workOrderNo: first.workOrderNo, productCode: first.productCode,
          productName: first.productName || first.productCode, country, note: null, productionLine: name,
          productionOnly: true, unplanned: true, planExplanation, allocations: [...new Set(entries.map((item) => item.date))].sort()
            .map((date) => ({ date, quantity: 0, sourceCell: "" })) });
      }
      continue;
    }
    const names = [...new Set(items.map((item) => productionLineFromDocument(item.documentNo) ?? UNMAPPED_PRODUCTION_LINE))].sort();
    for (const name of names) {
      if (primaryLines.get(key)?.has(name)) continue;
      const entries = items.filter((item) => (productionLineFromDocument(item.documentNo) ?? UNMAPPED_PRODUCTION_LINE) === name);
      const validPlan = plans.every((plan) => plan.allocations.every((allocation) => Number.isFinite(allocation.quantity) && allocation.quantity >= 0));
      let transfer = validPlan && entries.every((item) => Number.isFinite(item.quantity) && item.unit?.trim().toLocaleUpperCase("tr-TR") === "KOLİ")
        ? Math.max(0, entries.reduce((sum, item) => sum + item.quantity, 0)) : 0;
      let created = false;
      for (const plan of [...plans].sort((a, b) => a.sourceRow - b.sourceRow)) {
        const primary = routedPlans.get(plan.sourceRow);
        if (!primary) continue;
        const allocations: PackagingPlanRow["allocations"] = [];
        for (const allocation of [...primary.row.allocations].sort((a, b) => a.date.localeCompare(b.date))) {
          if (transfer <= 0 || allocation.quantity <= 0) continue;
          const moved = Math.min(transfer, allocation.quantity);
          allocations.push({ ...allocation, quantity: moved });
          allocation.quantity -= moved; transfer -= moved;
        }
        if (!allocations.length) continue;
        append(name, { ...plan, sourceRow: virtualRow--, excelSourceRow: plan.sourceRow, productionLine: name,
          plannedLineName: primary.row.plannedLineName, allocations });
        created = true;
      }
      if (!created) append(name, { ...template, sourceRow: virtualRow--, excelSourceRow: template.sourceRow,
        productionLine: name, productionOnly: true,
        plannedLineName: routedPlans.get(template.sourceRow)?.row.plannedLineName,
        allocations: [...new Set(entries.map((item) => item.date))].sort().map((date) => ({ date, quantity: 0, sourceCell: "" })) });
    }
  }
  for (const { name, row } of routedPlans.values()) {
    if (!row.productionLine) { append(name, row); continue; }
    const allocations = row.allocations.filter((allocation) => allocation.quantity !== 0);
    const entries = movements.get(packagingWorkOrderKey(row))?.filter((item) =>
      (productionLineFromDocument(item.documentNo) ?? UNMAPPED_PRODUCTION_LINE) === name) ?? [];
    if (!allocations.length && !entries.length) continue;
    append(name, { ...row, productionOnly: !allocations.length, allocations: allocations.length ? allocations
      : [...new Set(entries.map((item) => item.date))].sort().map((date) => ({ date, quantity: 0, sourceCell: "" })) });
  }
  for (const line of grouped.values()) {
    const combined = new Map<string, PackagingPlanRow>();
    for (const row of line.rows) {
      const key = JSON.stringify([packagingWorkOrderKey(row), row.productName, row.country, row.note,
        row.plannedLineName, row.productionLine, row.productionOnly, row.extraWork?.sourceCell]);
      const existing = combined.get(key);
      if (existing) {
        existing.allocations.push(...row.allocations.map((allocation) => ({ ...allocation })));
        existing.excelSourceRows = [...new Set([...(existing.excelSourceRows ?? []), row.excelSourceRow ?? row.sourceRow])];
      } else combined.set(key, { ...row, allocations: row.allocations.map((allocation) => ({ ...allocation })),
        excelSourceRows: row.unplanned ? [] : [row.excelSourceRow ?? row.sourceRow] });
    }
    line.rows = [...combined.values()];
  }
  return { ...snapshot, productionRouted: true, lines: [...grouped.values()].map((line) => ({ ...line,
    mappingStatus: line.rows.length ? "mapped" : line.mappingStatus,
    rows: line.rows.sort((a, b) => (a.excelSourceRow ?? a.sourceRow) - (b.excelSourceRow ?? b.sourceRow) || a.sourceRow - b.sourceRow) })) };
}
